import { MagicLinkService } from './magicLinkService';
import { Member } from '../types';
import { getFunctions, httpsCallable } from 'firebase/functions';

export class EmailService {
  
  // Envoyer un lien magique à un membre
  static async sendMagicLinkToMember(member: Member): Promise<string> {
    try {
      const magicLink = await MagicLinkService.generateMagicLink(member.id, member.email);
      
      // Ici, vous intégreriez votre service d'email (SendGrid, Mailgun, etc.)
      // Pour l'instant, on retourne juste le lien
      console.log(`Lien magique généré pour ${member.email}: ${magicLink}`);
      
      return magicLink;
    } catch (error) {
      console.error('Erreur lors de l\'envoi du lien magique:', error);
      throw error;
    }
  }

  // Envoyer des liens magiques en batch à tous les membres
  static async sendMagicLinksToAllMembers(members: Member[]): Promise<{
    success: number;
    errors: string[];
  }> {
    const results = {
      success: 0,
      errors: [] as string[]
    };

    for (const member of members) {
      try {
        await this.sendMagicLinkToMember(member);
        results.success++;
      } catch (error: any) {
        results.errors.push(`${member.email}: ${error.message}`);
      }
    }

    return results;
  }

  // Template d'email pour le lien magique
  static generateEmailTemplate(member: Member, magicLink: string): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Vote ASPROCOM - Lien de vote</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: #3b82f6; color: white; padding: 20px; text-align: center; }
          .content { padding: 20px; background: #f9fafb; }
          .button { 
            display: inline-block; 
            background: #3b82f6; 
            color: white; 
            padding: 12px 24px; 
            text-decoration: none; 
            border-radius: 6px; 
            margin: 20px 0;
          }
          .footer { padding: 20px; text-align: center; color: #666; font-size: 14px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>🗳️ Vote ASPROCOM</h1>
            <p>Élections des membres exécutifs</p>
          </div>
          
          <div class="content">
            <h2>Bonjour ${member.firstName} ${member.lastName},</h2>
            
            <p>Vous êtes invité(e) à participer aux élections des membres exécutifs de l'ASPROCOM.</p>
            
            <p><strong>Votre lien de vote personnel :</strong></p>
            <a href="${magicLink}" class="button">🗳️ Voter maintenant</a>
            
            <p><strong>Informations importantes :</strong></p>
            <ul>
              <li>Ce lien est personnel et sécurisé</li>
              <li>Il expire dans 24 heures</li>
              <li>Vos votes sont anonymes</li>
              <li>Vous ne pouvez voter qu'une seule fois</li>
            </ul>
            
            <p>Si vous ne pouvez pas cliquer sur le bouton, copiez et collez ce lien dans votre navigateur :</p>
            <p style="word-break: break-all; background: #e5e7eb; padding: 10px; border-radius: 4px;">
              ${magicLink}
            </p>
          </div>
          
          <div class="footer">
            <p>ASPROCOM - Association des Professionnels de la Communication</p>
            <p>Ce lien est sécurisé et ne peut être utilisé qu'une seule fois.</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  // Envoyer un email de rappel
  static async sendReminderEmail(member: Member): Promise<string> {
    // Générer un nouveau lien magique pour le rappel
    return await this.sendMagicLinkToMember(member);
  }

  // Envoyer des liens magiques pour un groupe via Firebase Functions
  static async sendGroupMagicLinks(
    groupId: string,
    groupName: string,
    members: Array<{
      id: string;
      email: string;
      firstName: string;
      lastName: string;
    }>
  ): Promise<{
    success: number;
    errors: Array<{ email: string; error: string }>;
  }> {
    try {
      const functions = getFunctions();
      const sendGroupMagicLinks = httpsCallable(functions, 'sendGroupMagicLinks');
      
      const result = await sendGroupMagicLinks({
        groupId,
        groupName,
        members
      });

      return result.data as {
        success: number;
        errors: Array<{ email: string; error: string }>;
      };
    } catch (error) {
      console.error('Erreur lors de l\'envoi des liens magiques:', error);
      throw error;
    }
  }

  // Tester la connexion SMTP via Firebase Functions
  static async testSMTPConnection(): Promise<boolean> {
    try {
      const functions = getFunctions();
      const testSMTP = httpsCallable(functions, 'testSMTPConnection');
      
      const result = await testSMTP();
      return (result.data as { success: boolean }).success;
    } catch (error) {
      console.error('Erreur de test SMTP:', error);
      return false;
    }
  }

  // Valider un lien magique via Firebase Functions
  static async validateMagicLink(token: string): Promise<{
    member: Member;
    groupId: string;
  }> {
    try {
      const functions = getFunctions();
      const validateMagicLink = httpsCallable(functions, 'validateMagicLink');
      
      const result = await validateMagicLink({ token });
      return result.data as {
        member: Member;
        groupId: string;
      };
    } catch (error) {
      console.error('Erreur de validation du lien magique:', error);
      throw error;
    }
  }
}
